import { useState, useRef, useEffect } from "react";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const DOW = ["S", "M", "T", "W", "T", "F", "S"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parse = (s) => { if (!s) return null; const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };

export function DatePickerField({ label, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const selected = parse(value);
  const [view, setView] = useState(() => selected || new Date());
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const year = view.getFullYear(), month = view.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const label_ = value ? parse(value) : null;
  const display = label_ ? `${String(label_.getDate()).padStart(2, "0")} ${MONTHS[label_.getMonth()].slice(0, 3)} ${label_.getFullYear()}` : "Select a date";

  return (
    <div ref={ref} className="relative">
      {label && <label className="errah-label block mb-2">{label}</label>}
      <button type="button" data-testid={testid} onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between bg-transparent border border-white/15 px-4 py-3 text-sm hover:border-[#C9A45C]/60 focus:border-[#C9A45C] transition-colors">
        <span className={value ? "text-white" : "text-gray-500"}>{display}</span>
        <CalendarIcon size={16} className="text-gray-500" />
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-[300px] bg-[#141414] border border-white/10 shadow-[0_8px_40px_rgba(0,0,0,0.7)] p-4" data-testid={`${testid}-panel`}>
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={() => setView(new Date(year, month - 1, 1))} className="p-1.5 text-gray-400 hover:text-[#C9A45C] transition-colors"><ChevronLeft size={16} /></button>
            <span className="font-display text-sm">{MONTHS[month]} {year}</span>
            <button type="button" onClick={() => setView(new Date(year, month + 1, 1))} className="p-1.5 text-gray-400 hover:text-[#C9A45C] transition-colors"><ChevronRight size={16} /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DOW.map((d, i) => <div key={i} className="text-center text-[10px] text-gray-600 py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, i) => {
              if (!day) return <div key={i} />;
              const date = new Date(year, month, day);
              const past = date < today;
              const isSel = selected && iso(date) === iso(selected);
              const isToday = iso(date) === iso(today);
              return (
                <button key={i} type="button" disabled={past}
                  onClick={() => { onChange(iso(date)); setOpen(false); }}
                  className={`h-8 w-8 text-sm transition-colors ${isSel ? "bg-[#C9A45C] text-black font-semibold" : past ? "text-gray-700 cursor-not-allowed" : isToday ? "border border-[#C9A45C]/50 text-white hover:bg-white/10" : "text-gray-300 hover:bg-white/10"}`}>
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
